import { FunctionComponent, useEffect, useState } from 'react'
import QuestionComponent from './QuestionComponent'
import FeedbackPageComponent from './FeedbackPageComponent'
import ResultComponent from './ResultComponent'
import { Spinner } from 'react-bootstrap'
import {
  FeedbackPage,
  QuizAnswer,
  QuizData,
  QuizSubmission,
  QuizResult,
  QuestionType,
} from 'Frontend/scripts/models/foodHabits.model'
import { submitQuiz } from 'Frontend/scripts/services/foodHabits.service'
import { t } from 'i18next'

type Props = {
  quizData: QuizData
}

const QuizContainer: FunctionComponent<Props> = ({ quizData }) => {
  const [currentStep, setCurrentStep] = useState(0)
  const [startGuide, setStartGuide] = useState(false)
  const [answers, setAnswers] = useState<{ [key: string]: number | number[] }>({})
  const [showFeedback, setShowFeedback] = useState(false)
  const [feedbackPage, setFeedbackPage] = useState<FeedbackPage | null>(null)
  const [result, setResult] = useState<QuizResult | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [questionType, setQuestionType] = useState('')

  useEffect(() => {
    const initialAnswers: { [key: string]: number | number[] } = {}
    quizData.questions.forEach((question) => {
      initialAnswers[question.id] = question.type === 'OneOrMoreQuestionPage' ? [] : 0
    })
    setAnswers(initialAnswers)
  }, [quizData])

  useEffect(() => {
    const currentClassName = toQuestionTypeClass(quizData.questions[currentStep].type)
    setQuestionType(currentClassName)
  }, [quizData, currentStep])

  const handleSubmit = async () => {
    const formattedAnswers: QuizAnswer[] = Object.entries(answers).map(([questionId, answer]) => {
      const question = quizData.questions[Number(questionId)]
      let score: number;

      if (Array.isArray(answer)) {
        score = answer.reduce((sum, index) => sum + (question.answers?.[index]?.score ?? 0), 0)
      } else if ( question.type === 'SliderQuestionPage' && question.sliderOptions) {
        score = question.sliderOptions[answer]?.score ?? 0;
      } else {
        score = answer
      }
       
      return {
        questionId: Number(questionId),
        score,
        categoryId: question?.categoryId ?? 0,
      }
    })

    const submission: QuizSubmission = {
      startPageId: quizData.contentId,
      answers: formattedAnswers,
    }

    setIsSubmitting(true)

    try {
      const data = await submitQuiz(submission)
      setResult(data)
    } catch (error: any) {
      console.error('Error submitting quiz:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const nextStep = () => {
    const question = quizData.questions[currentStep]

    if (question.feedback?.feedbackInterval && !showFeedback) {
      const answer = answers[question.id]
      const score = Array.isArray(answer)
        ? answer.reduce((sum, index) => sum + (question.answers?.[index]?.score ?? 0), 0)
        : answer

      const matchingFeedback = question.feedback.feedbackInterval.find(
        (interval) => score >= interval.lowerInterval && score <= interval.upperInterval,
      )

      if (matchingFeedback) {
        setFeedbackPage({ ...matchingFeedback, showBackLink: question.feedback.showBackLink })
        setShowFeedback(true)
        return
      }
    }

    setShowFeedback(false)
    setCurrentStep((prev) => prev + 1)
  }

  const prevStep = () => {
    setShowFeedback(false)
    setCurrentStep((prev) => prev - 1)
  }

  const handleAnswer = (questionId: string, value: number | number[]) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value ?? (Array.isArray(value) ? [] : 0) }))
  }

  return (
    <>
      {result !== null ? (
        <ResultComponent result={result} />
      ) : (
        <div className="quiz" style={{ background: `#111a23 url(${quizData.backgroundImage})` }}>
          <div className="quiz__inner">
            {!startGuide ? (
              <div className="row">
                <div className="col-10 offset-1 quiz__content-wrapper start">
                  <h1 className="quiz__title">{quizData.title}</h1>
                  <span className="quiz__preamble" dangerouslySetInnerHTML={{ __html: quizData.text }}></span>
                  <nav className="quiz__navigation" aria-label="Navigera">
                    <button className="btn btn-primary quiz__button" onClick={() => setStartGuide(true)}>
                      {t('start-guide')}
                    </button>
                  </nav>
                </div>
              </div>
            ) : showFeedback && feedbackPage ? (
              <>
                <div className="quiz__current-questions">
                  {t('current-page', { currentPageNr: currentStep + 1, allPagesNr: quizData.questions.length })}
                </div>
                <div className="row">
                  <div className="col-10 offset-1 quiz__content-wrapper feedback">
                    <FeedbackPageComponent feedbackPage={feedbackPage} />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="quiz__current-questions">
                  {t('current-page', { currentPageNr: currentStep + 1, allPagesNr: quizData.questions.length })}
                </div>
                <div className="row">
                  <div className={`col-10 offset-1 quiz__content-wrapper ${questionType}`}>
                    <QuestionComponent
                      question={quizData.questions[currentStep]}
                      answers={answers}
                      handleAnswer={handleAnswer}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {startGuide && (
            <nav className="quiz__navigation" aria-label="Navigera">
              {currentStep > 0 && !result && !showFeedback && (
                <button className="btn quiz__button quiz__button--previous" onClick={prevStep}>
                  {t('previous')}
                </button>
              )}
              {currentStep < quizData.questions.length - 1 ? (
                <button className="btn btn-primary quiz__button" onClick={nextStep}>
                  {t('next')}
                </button>
              ) : (
                <button className="btn btn-primary quiz__button" onClick={handleSubmit} disabled={isSubmitting}>
                  {isSubmitting ? <Spinner animation="border" role="status" /> : <>{t('show-result')}</>}
                </button>
              )}
            </nav>
          )}
        </div>
      )}
    </>
  )
}

const toQuestionTypeClass = (type: QuestionType) => {
  if (type === 'SliderQuestionPage') return 'slider-type'
  if (type === 'InformationPage') return 'info-type'
  if (type === 'NumberOfDaysaWeekQuestionPage') return 'days-a-week-type'
  if (type === 'OneOrMoreQuestionPage') return 'one-or-more-type'
  return ''
}

export default QuizContainer
